import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PlayerAvatar } from "./PlayerAvatar";
import { StandingsExportCard } from "./StandingsExportCard";
import { toast } from "sonner";
import { Search, TrendingUp, Download, Share2, Loader2, Crown } from "lucide-react";

function FormBadges({ form }) {
  const padded = [...form];
  while (padded.length < 5) padded.unshift(null);
  return (
    <div className="flex gap-1 justify-center">
      {padded.map((f, i) => (
        <span
          key={i}
          className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center font-mono ${
            f === "V"
              ? "bg-emerald-500/20 text-emerald-400"
              : f === "P"
              ? "bg-rose-500/20 text-rose-400"
              : "bg-slate-800 text-slate-600"
          }`}
        >
          {f || "–"}
        </span>
      ))}
    </div>
  );
}

export function StandingsView({ standings, onSelectPlayer }) {
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);
  const filtered = standings.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const medalBorder = ["border-l-amber-400", "border-l-slate-300", "border-l-amber-700"];

  const renderCanvas = async () => {
    const node = exportRef.current;
    if (!node) return null;
    return html2canvas(node, { backgroundColor: "#090D16", scale: 2, logging: false });
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const canvas = await renderCanvas();
      const link = document.createElement("a");
      link.download = `classifica-american-padel-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Immagine scaricata");
    } catch (e) {
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setExporting(true);
    try {
      const canvas = await renderCanvas();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "classifica-american-padel.png", { type: "image/png" });
      const canShareFiles =
        typeof navigator.canShare === "function" && navigator.canShare({ files: [file] });
      if (canShareFiles) {
        try {
          await navigator.share({
            files: [file],
            title: "Classifica American Padel 2026",
            text: "Classifica aggiornata del campionato 🎾",
          });
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = "classifica-american-padel.png";
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Immagine salvata (condivisione diretta non disponibile)");
    } catch (e) {
      toast.error("Errore durante la condivisione");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="standings-view">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400">
            Classifica Generale
          </div>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white">
            Classifica Individuale
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="export-standings-btn"
            onClick={handleDownload}
            disabled={exporting || !standings.length}
            size="sm"
            className="bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700 font-semibold uppercase text-xs tracking-wide"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 sm:mr-1.5" />}
            <span className="hidden sm:inline">Scarica</span>
          </Button>
          <Button
            data-testid="share-standings-btn"
            onClick={handleShare}
            disabled={exporting || !standings.length}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold uppercase text-xs tracking-wide"
          >
            <Share2 className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Condividi</span>
          </Button>
          <div className="relative w-40 sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              data-testid="standings-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca…"
              className="pl-9 bg-slate-900 border-slate-700 text-slate-100 focus-visible:ring-emerald-500"
            />
          </div>
        </div>
      </div>

      <StandingsExportCard ref={exportRef} standings={standings} />

      <div className="ap-glass rounded-2xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[760px]">
            <thead>
              <tr className="bg-slate-950/70 border-b border-white/10 font-mono text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-3 py-3 text-center w-12">#</th>
                <th className="px-3 py-3">Giocatore</th>
                <th className="px-2 py-3 text-center" title="Giocate">G</th>
                <th className="px-2 py-3 text-center" title="Vinte">V</th>
                <th className="px-2 py-3 text-center" title="Perse">P</th>
                <th className="px-2 py-3 text-center">Set</th>
                <th className="px-2 py-3 text-center">Games</th>
                <th className="px-2 py-3 text-center">Diff.</th>
                <th className="px-3 py-3 text-center">Ultime 5</th>
                <th className="px-3 py-3 text-center">Punti</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const podiumBg = ["bg-amber-500/[0.07]", "bg-slate-400/[0.06]", "bg-amber-700/[0.07]"];
                return (
                <tr
                  key={p.player_id}
                  data-testid={`standings-table-row-${p.position}`}
                  onClick={() => onSelectPlayer(p.player_id)}
                  className={`border-b border-white/5 border-l-4 cursor-pointer transition-colors hover:bg-slate-800/60 ${
                    p.position <= 3 ? `${medalBorder[p.position - 1]} ${podiumBg[p.position - 1]}` : "border-l-transparent"
                  }`}
                >
                  <td className="px-3 py-3 text-center font-display font-black text-lg text-slate-300">
                    {p.position === 1 ? (
                      <Crown className="w-5 h-5 mx-auto text-amber-400" fill="currentColor" />
                    ) : (
                      p.position
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <PlayerAvatar id={p.player_id} name={p.name} size={32} />
                      <span className="font-semibold text-white whitespace-nowrap">{p.name}</span>
                      {(() => {
                        let st = 0;
                        for (let i = p.form.length - 1; i >= 0; i--) {
                          if (p.form[i] === "V") st++;
                          else break;
                        }
                        return st >= 2 ? (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-orange-500/15 text-orange-400 text-[10px] font-bold font-mono">
                            🔥{st}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </td>
                  <td className="px-2 py-3 text-center font-mono text-slate-300">{p.played}</td>
                  <td className="px-2 py-3 text-center font-mono text-emerald-400 font-semibold">{p.wins}</td>
                  <td className="px-2 py-3 text-center font-mono text-rose-400 font-semibold">{p.losses}</td>
                  <td className="px-2 py-3 text-center font-mono text-slate-300">
                    {p.sets_won}-{p.sets_lost}
                  </td>
                  <td className="px-2 py-3 text-center font-mono text-slate-300">
                    {p.games_won}-{p.games_lost}
                  </td>
                  <td
                    className={`px-2 py-3 text-center font-mono font-semibold ${
                      p.games_diff > 0
                        ? "text-emerald-400"
                        : p.games_diff < 0
                        ? "text-rose-400"
                        : "text-slate-400"
                    }`}
                  >
                    {p.games_diff > 0 ? "+" : ""}
                    {p.games_diff}
                  </td>
                  <td className="px-3 py-3">
                    <FormBadges form={p.form} />
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="inline-flex items-center justify-center min-w-8 px-2 py-1 rounded-lg bg-emerald-500/15 font-mono font-black text-emerald-400 text-lg">
                      {p.points}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <p className="flex items-center gap-2 text-xs text-slate-500">
        <TrendingUp className="w-3.5 h-3.5" />
        Tocca un giocatore per vedere le statistiche dettagliate. A parità di punti vale il minor
        numero di games subiti.
      </p>
    </div>
  );
}
